import { useEffect, useState } from 'react';
import { getMyAccount, updateMarketingConsent } from '../api/client';

function consentLabel(consent) {
  if (consent === true) return 'Granted';
  if (consent === false) return 'Refused';
  return 'Not answered';
}

function AccountPage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;
    getMyAccount()
      .then((result) => {
        if (active) setUser(result);
      })
      .catch((loadError) => {
        if (active) setError(loadError?.message || 'Could not load your account.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const changeConsent = async (consent) => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const result = await updateMarketingConsent(consent);
      setUser((current) => ({ ...current, marketing_consent: result.consent }));
      setSaved(true);
    } catch (updateError) {
      // updateMarketingConsent always attaches a message -- the server's detail,
      // or its own default -- so a status-specific fallback here was unreachable
      // dead code. The server already explains a 403 ("Publication consent for
      // an under-18 student is given by their guardian"); keep one generic guard
      // only in case that client contract ever changes.
      setError(updateError?.message || 'Could not update publication permission. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="page page-shell"><p>Loading account...</p></div>;
  }

  if (!user) {
    return (
      <div className="page page-shell page-stack">
        <section className="page-header">
          <p className="eyebrow">Account</p>
          <h1>Publication permission</h1>
        </section>
        <p role="alert" className="notice notice-error">
          {error || 'Could not load your account. Please try again.'}
        </p>
      </div>
    );
  }

  const canManage = user.can_manage_marketing_consent !== false;

  return (
    <div className="page page-shell page-stack">
      <section className="page-header">
        <p className="eyebrow">Account</p>
        <h1>Publication permission</h1>
        <p>Control whether Sumrise may publicly quote unnamed feedback about the app.</p>
      </section>

      <section className="card account-consent-card">
        <p>Current answer: <strong>{consentLabel(user?.marketing_consent)}</strong></p>
        {!canManage ? (
          <p className="notice notice-info">
            A parent or guardian holds publication permission for an under-18 student.
            You cannot change it from this account.
          </p>
        ) : (
          <fieldset disabled={saving}>
            <legend>May Sumrise publicly quote your unnamed feedback?</legend>
            <label>
              <input
                type="radio"
                name="publication-permission"
                checked={user?.marketing_consent === true}
                onChange={() => changeConsent(true)}
              />{' '}
              Yes, I give permission
            </label>
            <label>
              <input
                type="radio"
                name="publication-permission"
                checked={user?.marketing_consent === false}
                onChange={() => changeConsent(false)}
              />{' '}
              No, I do not give permission
            </label>
          </fieldset>
        )}
        {user?.role === 'PARENT' && user?.has_linked_children ? (
          <p className="muted-text">
            Your answer also applies to feedback written by every child linked to your account.
          </p>
        ) : null}
        {saved ? <p role="status" className="notice notice-success">Publication permission updated.</p> : null}
        {error ? <p role="alert" className="notice notice-error">{error}</p> : null}
      </section>
    </div>
  );
}

export default AccountPage;
